import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import {
  ExTooltip,
  ExIconButton,
  IconButtonType,
  IconButtonFlavor,
  IconButtonSize,
  TooltipPosition,
  TooltipAlignment,
} from '@boomi/exosphere';
import sanitizeHtml from '@common/dompurify/sanitizeHtml';

interface TooltipWithLinkProps {
  infoTextKey: string;
  linkTextKey: string;
  position?: typeof TooltipPosition[keyof typeof TooltipPosition];
  alignment?: typeof TooltipAlignment[keyof typeof TooltipAlignment];
  trigger?: 'click' | 'hover focus';
  testId: string;
  iconButtonTestId: string;
}

function TooltipWithLink({
  infoTextKey,
  linkTextKey,
  position = TooltipPosition.BOTTOM,
  alignment = TooltipAlignment.MIDDLE,
  trigger = 'click',
  testId,
  iconButtonTestId,
}: Readonly<TooltipWithLinkProps>) {
  const [t] = useTranslation('common');

  const tooltipLinkHtml = useMemo(
    () => sanitizeHtml(t(linkTextKey)),
    [t, linkTextKey],
  );

  const alignmentValue = alignment;

  return (
    <>
      <style>
        {`
        .link-container {
         line-height: var(--exo-spacing-standard) !important;
        }
        .link-container a {
          display: inline-flex !important;
          color: var(--exo-color-font-link) !important;
          font-size: var(--exo-font-size-micro) !important;
          font-weight: var(--exo-font-weight-semi-bold) !important;
        }
        .link-container a:hover, a:focus {
          outline: none !important;
          color: var(--exo-color-font-link-hover) !important;
        }
        .link-container svg {
          display: none !important;
        }
      `}
      </style>
      <ExTooltip
        position={position}
        alignment={alignmentValue}
        trigger={trigger}
        keepPopupOpen
      >
        <div className="link-container">
          {t(infoTextKey)}
          {' '}
          <span
            data-testid={testId}
            data-locator-id={testId}
            dangerouslySetInnerHTML={{
              __html: tooltipLinkHtml,
            }}
          />
        </div>
        <ExIconButton
          slot="anchor"
          icon="information"
          hideBrowserTooltip
          type={IconButtonType.TERTIARY}
          flavor={IconButtonFlavor.BRANDED}
          size={IconButtonSize.XSMALL}
          label="Information"
          circular
          style={{
            marginLeft: 'var(--exo-spacing-none)',
            marginRight: 'var(--exo-spacing-none)',
          }}
          data-testid={iconButtonTestId}
          data-locator-id={iconButtonTestId}
        />
      </ExTooltip>
    </>
  );
}

export default TooltipWithLink;
